import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('admin_refresh_token');
      
      if (!refreshToken) {
        // No refresh token available, redirect to login
        console.warn('No refresh token found, clearing tokens and redirecting to login');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_token_expires_at');
        window.location.href = '/admin';
        return Promise.reject(error);
      }

      try {
        console.log('Attempting to refresh access token...');
        const response = await axios.post(`${API_BASE_URL}/api/admin/refresh`, {
          refresh_token: refreshToken
        });

        const { access_token, refresh_token: new_refresh_token, expires_in } = response.data;
        
        // Store new tokens
        localStorage.setItem('admin_token', access_token);
        localStorage.setItem('admin_refresh_token', new_refresh_token);
        
        // Calculate and store expiration time
        const expiresAt = new Date(Date.now() + (expires_in * 1000));
        localStorage.setItem('admin_token_expires_at', expiresAt.toISOString());
        
        console.log('Token refreshed successfully');
        
        // Update the authorization header for the failed request
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        // Process any queued requests
        processQueue(null, access_token);
        
        // Retry the original request
        return axiosInstance(originalRequest);
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_token_expires_at');
        
        processQueue(refreshError, null);
        
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/admin') {
          window.location.href = '/admin';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Function to check if token is about to expire (within 5 minutes)
export const isTokenExpiringSoon = () => {
  const expiresAt = localStorage.getItem('admin_token_expires_at');
  if (!expiresAt) return false;
  
  const expiration = new Date(expiresAt);
  const now = new Date();
  const fiveMinutesFromNow = new Date(now.getTime() + (5 * 60 * 1000));
  
  return expiration <= fiveMinutesFromNow;
};

// Function to proactively refresh token if it's expiring soon
export const refreshTokenIfNeeded = async () => {
  if (isTokenExpiringSoon() && !isRefreshing) {
    const refreshToken = localStorage.getItem('admin_refresh_token');
    if (refreshToken) {
      try {
        console.log('Proactively refreshing token...');
        const response = await axios.post(`${API_BASE_URL}/api/admin/refresh`, {
          refresh_token: refreshToken
        });

        const { access_token, refresh_token: new_refresh_token, expires_in } = response.data;
        
        localStorage.setItem('admin_token', access_token);
        localStorage.setItem('admin_refresh_token', new_refresh_token);
        
        const expiresAt = new Date(Date.now() + (expires_in * 1000));
        localStorage.setItem('admin_token_expires_at', expiresAt.toISOString());
        
        console.log('Token proactively refreshed');
        return true;
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        return false;
      }
    }
  }
  return true;
};

export default axiosInstance;